/**
 * PoiForm — bottom sheet for creating/editing a Point of Interest.
 *
 * Sprint 3 Phase 3.6 per POI_ARCHITECTURE.md §11.
 *
 * Props:
 *   latlng       — { lat, lng } — map position (for create)
 *   poi          — existing POI object (for edit, optional)
 *   defaultType  — optional default poi_type
 *   token        — JWT
 *   fields       — available fields for linkage [{id, name}, ...]
 *   onSave       — (savedPoi) => void
 *   onCancel     — () => void
 */
import { useState, useId, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

// 9 categories per POI_ARCHITECTURE.md §2
const POI_CATEGORIES = [
  { value: 'zufahrt',        icon: '🚪', labelKey: 'poi.cat.zufahrt' },
  { value: 'lagerung',       icon: '📦', labelKey: 'poi.cat.lagerung' },
  { value: 'infrastruktur',  icon: '⚡', labelKey: 'poi.cat.infrastruktur' },
  { value: 'logistik',       icon: '🚚', labelKey: 'poi.cat.logistik' },
  { value: 'hindernisse',    icon: '⚠️', labelKey: 'poi.cat.hindernisse' },
  { value: 'naturschutz',    icon: '🌿', labelKey: 'poi.cat.naturschutz' },
  { value: 'operativ',       icon: '🔧', labelKey: 'poi.cat.operativ' },
  { value: 'sicherheit',     icon: '🛡️', labelKey: 'poi.cat.sicherheit' },
  { value: 'sonstiges',      icon: '📍', labelKey: 'poi.cat.sonstiges' },
];

const POI_CLASSES = [
  { value: 'permanent', labelKey: 'poi.class.permanent' },
  { value: 'operative', labelKey: 'poi.class.operative' },
];

const SHARING_CLASSES = [
  { value: 'tenant_internal',   labelKey: 'poi.sharing.tenantInternal' },
  { value: 'invited_only',      labelKey: 'poi.sharing.invitedOnly' },
  { value: 'shared_via_link',   labelKey: 'poi.sharing.sharedViaLink' },
  { value: 'public',            labelKey: 'poi.sharing.public' },
];

export default function PoiForm({ latlng, poi, defaultType = 'other', token, fields = [], onSave, onCancel }) {
  const { t } = useTranslation();
  const titleId = useId();
  const firstInputRef = useRef(null);
  const isEdit = !!poi?.id;

  useEffect(() => {
    if (firstInputRef.current) firstInputRef.current.focus();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  // Form state
  const [name, setName] = useState(poi?.name || '');
  const [notes, setNotes] = useState(poi?.notes || '');
  const [poiCategory, setPoiCategory] = useState(poi?.poi_category || '');
  const [poiClass, setPoiClass] = useState(poi?.poi_class || 'permanent');
  const [sharingClass, setSharingClass] = useState(poi?.sharing_class || 'tenant_internal');
  const [selectedFieldIds, setSelectedFieldIds] = useState(
    () => (poi?.linked_fields || []).map(f => f.field_id || f.id).filter(Boolean)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const handlePhotoSuggest = async (file) => {
    if (!file) return;
    setAiLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await apiClient.post('/api/v1/aif/pois/suggest-from-photo', formData, {
        queueOnOffline: true,
        entityType: 'poi-photo',
      });
      const s = result.data;
      if (!name && s.suggested_name) setName(s.suggested_name);
      if (!poiCategory && s.category) setPoiCategory(s.category);
      if (!notes && s.description) setNotes(s.description);
    } catch (e) {
      setError(t('poi.aiError', 'Foto-Analyse fehlgeschlagen. Bitte manuell ausfüllen.'));
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!poiCategory) { setError(t('poi.errorCategoryRequired', 'Kategorie ist erforderlich')); return; }
    setSaving(true);
    setError(null);
    try {
      const url = isEdit ? `/api/v1/aif/pois/${poi.id}` : '/api/v1/aif/pois';
      const payload = {
        name: name.trim() || null,
        notes: notes.trim() || null,
        poi_category: poiCategory,
        sharing_class: sharingClass,
        ...(isEdit ? {} : {
          poi_class: poiClass,
          poi_type: defaultType,
          latitude: latlng?.lat ?? poi?.latitude,
          longitude: latlng?.lng ?? poi?.longitude,
          field_ids: selectedFieldIds.length > 0 ? selectedFieldIds : undefined,
        }),
      };
      const result = isEdit
        ? await apiClient.patch(url, payload)
        : await apiClient.post(url, payload, { entityType: 'poi' });
      const savedPoi = result.data;
      onSave?.(savedPoi);
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleField = (fid) => {
    setSelectedFieldIds(prev =>
      prev.includes(fid) ? prev.filter(id => id !== fid) : [...prev, fid]
    );
  };

  return (
    <div
      data-testid="poi-form"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className="absolute bottom-0 left-0 right-0 z-[var(--aif-z-bottom-sheet)] bg-[var(--aif-bg-surface)] rounded-t-2xl
                 shadow-2xl border-t border-[var(--aif-border-default)] max-h-[80vh] overflow-y-auto"
    >
      {/* Drag handle */}
      <div className="flex justify-center pt-2 pb-1 sticky top-0 bg-[var(--aif-bg-surface)]" aria-hidden="true">
        <div className="w-8 h-1 rounded-full bg-[var(--aif-border-default)]" />
      </div>

      <div className="px-4 pb-6 space-y-4">
        <h2 id={titleId} className="text-base font-semibold text-[var(--aif-text-primary)]">
          {isEdit ? t('poi.editTitle', 'POI bearbeiten') : t('poi.markTitle')}
        </h2>

        {error && (
          <div className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>
        )}

        {/* AI photo suggestion — create mode only */}
        {!isEdit && (
          <div className="border-2 border-dashed border-[var(--aif-border-default)] rounded-xl p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm">📷</span>
              <span className="text-xs font-medium text-[var(--aif-text-secondary)]">
                {t('poi.photoSuggest', 'Foto für AI-Vorschlag')}
              </span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={aiLoading}
                onChange={e => handlePhotoSuggest(e.target.files?.[0])}
              />
              {aiLoading && (
                <span className="ml-auto flex items-center gap-1 text-xs text-[var(--aif-text-muted)]">
                  <span className="w-3 h-3 border-2 border-[var(--aif-color-primary)] border-t-transparent rounded-full animate-spin" />
                  {t('poi.analyzing', 'Analysiere...')}
                </span>
              )}
            </label>
          </div>
        )}

        {/* Category selector — 9 categories in 3x3 grid */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-2">{t('poi.category', 'Kategorie')}</label>
          <div className="grid grid-cols-3 gap-2">
            {POI_CATEGORIES.map(cat => (
              <button
                key={cat.value}
                type="button"
                data-testid={`poi-cat-${cat.value}`}
                onClick={() => setPoiCategory(cat.value)}
                disabled={isEdit}
                className={`flex flex-col items-center justify-center gap-1 min-h-[56px] py-2 rounded-xl border text-xs font-medium transition
                  ${poiCategory === cat.value
                    ? 'border-[var(--aif-color-primary)] bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)]'
                    : 'border-[var(--aif-border-default)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)]'
                  } ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <span className="text-lg">{cat.icon}</span>
                <span>{t(cat.labelKey, cat.value)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Class selector — only on create */}
        {!isEdit && (
          <div>
            <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('poi.class', 'Klasse')}</label>
            <div className="flex gap-2">
              {POI_CLASSES.map(cls => (
                <button
                  key={cls.value}
                  type="button"
                  onClick={() => setPoiClass(cls.value)}
                  className={`flex-1 py-2 rounded-xl border text-sm font-medium transition
                    ${poiClass === cls.value
                      ? 'border-[var(--aif-color-primary)] bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)]'
                      : 'border-[var(--aif-border-default)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)]'
                    }`}
                >
                  {t(cls.labelKey, cls.value)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('poi.nameLabel')}</label>
          <input
            ref={firstInputRef}
            data-testid="poi-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('poi.namePlaceholder')}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('poi.notesLabel')}</label>
          <textarea
            data-testid="poi-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('poi.notesPlaceholder')}
            rows={2}
            className="w-full px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)] resize-none"
          />
        </div>

        {/* Sharing class */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('poi.sharing', 'Sichtbarkeit')}</label>
          <select
            value={sharingClass}
            onChange={(e) => setSharingClass(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          >
            {SHARING_CLASSES.map(sc => (
              <option key={sc.value} value={sc.value}>{t(sc.labelKey, sc.value)}</option>
            ))}
          </select>
        </div>

        {/* Field linkage — only on create, only if fields provided */}
        {!isEdit && fields.length > 0 && (
          <div>
            <label className="block text-xs text-[var(--aif-text-muted)] mb-2">
              {t('poi.linkFields', 'Mit Feldern verknüpfen')}
            </label>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {fields.map(f => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => toggleField(f.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition
                    ${selectedFieldIds.includes(f.id)
                      ? 'bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)]/30'
                      : 'text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)]'
                    }`}
                >
                  {selectedFieldIds.includes(f.id) ? '✓ ' : ''}{f.name}
                  {f.area_ha ? ` (${f.area_ha} ha)` : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 pt-1">
          <button
            data-testid="poi-form-cancel"
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       text-[var(--aif-text-secondary)] text-sm font-medium
                       hover:bg-[var(--aif-bg-elevated)] transition disabled:opacity-50"
          >
            {t('form.cancel')}
          </button>
          <button
            data-testid="poi-form-save"
            type="button"
            onClick={handleSave}
            disabled={saving || !poiCategory}
            className="flex-1 py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                       hover:opacity-90 transition disabled:opacity-50"
          >
            {saving ? t('poi.saving') : isEdit ? t('poi.update', 'Aktualisieren') : t('poi.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
